<template>
  <v-autocomplete
    ref="orgAutocomplete"
    v-model="model"
    :items="items"
    :search-input.sync="search"
    :filter="() => true"
    :loading="loading"
    hide-details
    item-text="name"
    item-value="id"
    :label="_label"
    :prepend-icon="icon"
    return-object
    :style="{ maxWidth: maxWidth }"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <template #item="{ item }">
      <v-list-item-icon class="mr-2">
        <OrganizationIcon :organization="item" small />
      </v-list-item-icon>
      <v-list-item-content>
        {{ item.name }}
      </v-list-item-content>
    </template>
    <template #no-data>
      <v-list-item>
        <v-list-item-content>
          <v-list-item-title class="grey--text">
            {{ noDataMessage }}
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </template>
  </v-autocomplete>
</template>

<script>
import { LIST_ORGANIZATION_DESCENDANTS } from "../../graphql/organizations";

import OrganizationIcon from "./OrganizationIcon.vue";
import { debounce } from "@tod-ui/helpers/utils";
import gql from "graphql-tag";

const FIND_ORGANIZATION = gql`
  query Organization($id: ID!) {
    organization(id: $id) {
      id
      name
      type
      subtype
    }
  }
`;

export default {
  name: "OrganizationAutocomplete",
  components: { OrganizationIcon },
  props: {
    filter: {
      type: Object,
      default: () => {},
    },
    organization: {
      type: Object,
      default: null,
    },
    // When provided, the list is this pre-fetched set (filtered client-side by
    // the search text) instead of descendants of a single `organization`. Used
    // to feed the union of a multi-org user's accessible facilities.
    sourceItems: {
      type: Array,
      default: null,
    },
    value: {
      type: Object,
      default: null,
    },
    icon: {
      type: String,
      default: "mdi-magnify",
    },
    label: {
      type: String,
      default: "",
    },
    prefetch: {
      type: Boolean,
      default: false,
    },
    maxWidth: {
      type: String,
      default: "25%",
    },
  },
  data() {
    return {
      items: [],
      descendants: null,
      initialValue: null,
      search: "",
      minSearchLength: 3,
      loading: false,
    };
  },
  computed: {
    searchParams() {
      const params = {
        ...this.filter,
      };

      delete params.exclude;
      for (let k in params) {
        if (params[k] === null) {
          delete params[k];
        }
      }

      return params;
    },
    exclude() {
      return this.filter?.exclude;
    },
    _label() {
      if (this.label) return this.label;
      return this.organization
        ? `Search ${this.organization.name} organizations`
        : "Search organizations";
    },
    model: {
      get() {
        return this.initialValue;
      },
      set(val) {
        if (val) {
          this.initialValue = val;
          this.$emit("input", val);
        }
      },
    },
    // Contextual empty-state copy: distinguish "still fetching" and "keep typing"
    // from a genuinely empty result, instead of one ambiguous "not found".
    noDataMessage() {
      if (this.loading) return "Searching…";
      const query = (this.search || "").trim();
      // Server-search fields (no prefetch, no in-memory source) need typed input
      // — guide the user instead of showing a bare "not found" before they type.
      if (
        !this.prefetch &&
        !this.sourceItems &&
        query.length < this.minSearchLength
      ) {
        return query.length === 0
          ? "Type to search"
          : `Type at least ${this.minSearchLength} characters to search`;
      }
      return this.$attrs["no-data-text"] || "No results found";
    },
  },
  watch: {
    // Reflect a selection that arrives (or changes) after mount — e.g. the header
    // facility switcher whose currentFacility resolves asynchronously on refresh.
    value(newVal) {
      if (newVal?.id) {
        if (newVal.id !== this.initialValue?.id) this.setInitialOrganization();
      } else {
        this.initialValue = null;
      }
    },
    search(value) {
      const searchText = value?.trim();
      if (
        !searchText ||
        (!this.prefetch && searchText.length < this.minSearchLength) ||
        searchText === this.initialValue?.name
      )
        return;

      this.debouncedSearch(searchText);
    },
  },
  beforeMount() {
    if (this.value?.id) {
      this.setInitialOrganization();
    }
    if (this.sourceItems) {
      this.items = this.sourceItems;
    } else if (this.prefetch) {
      this.searchOrganizationDescendants("");
    }
    this.debouncedSearch = debounce(this.searchOrganizationDescendants);
  },
  beforeDestroy() {
    this.debouncedSearch.cancel();
  },
  methods: {
    async searchOrganizationDescendants(searchText) {
      // Pre-fetched source list (multi-org union): filter client-side, no query.
      if (this.sourceItems) {
        const q = (searchText || "").toLowerCase();
        this.items = q
          ? this.sourceItems.filter((org) => org.name.toLowerCase().includes(q))
          : this.sourceItems;
        return;
      }
      if (this.prefetch && this.descendants !== null) {
        this.items =
          searchText === ""
            ? this.descendants
            : this.descendants.filter((org) =>
                org.name.toLowerCase().includes(searchText.toLowerCase())
              );
      } else {
        this.loading = true;
        try {
          const {
            data: { organizationDescendants },
          } = await this.$apollo.query({
            query: LIST_ORGANIZATION_DESCENDANTS,
            variables: {
              id: this.organization.id,
              searchText,
              ...this.searchParams,
            },
          });

          this.descendants = organizationDescendants
            .filter((org) =>
              this.exclude && Array.isArray(this.exclude)
                ? !this.exclude.includes(org.id)
                : true
            )
            .map((org) => ({
              id: org.id,
              name: org.name,
              type: org.type,
            }));

          this.items = this.descendants;
          if (this.prefetch) {
            this.$refs.orgAutocomplete.isMenuActive = true;
          }
        } catch (error) {
          console.error(error);
        } finally {
          this.loading = false;
        }
      }
    },
    async setInitialOrganization() {
      let organization = {};
      if (this.value?.id && !this.value?.name) {
        try {
          const result = await this.$apollo.query({
            query: FIND_ORGANIZATION,
            variables: {
              id: this.value.id,
            },
          });
          organization = result.data.organization;
        } catch (error) {
          console.error(error);
        }
      } else {
        organization = this.value;
      }

      this.initialValue = {
        id: organization.id,
        name: organization.name,
        type: organization.type,
      };
      this.items = [this.initialValue];
    },
  },
};
</script>
